import { styled } from '@mui/material';
import APIRoleBasedRouting from 'app/APIRoleBasedRouting';
import ProviderWrapper from 'app/ProviderWrapper';
import axios from 'axios';
import InstructorHugoChat from 'components/Chat/instructor-hugo';
import Header from 'components/Header';
import LogoOnlyHeader from 'components/Header/LogoOnlyHeader';
import { useAuth } from 'contexts/auth';
import { useViewport } from 'contexts/viewport';
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { captureApiFailure, captureApiSuccess } from 'services/activity';
import { Box, Loading, Stack } from 'shell/components';
import { expireAuthSession } from 'utils/auth';

axios.defaults.baseURL = process.env.REACT_APP_API_ENDPOINT;
axios.interceptors.response.use(
    (response) => {
        captureApiSuccess(response);
        return response.data;
    },
    (error) => {
        const { response = {} } = error;
        captureApiFailure(error);
        if (response.status === 401) {
            expireAuthSession();
        }
        return Promise.reject(response);
    }
);

const noHeaderRoutes = [
    '/api-launch',
    '/components',
    '/course-set-up',
    '/lti/deep-link-picker',
    '/lti-incomplete',
    '/lti-launch',
    '/profile-setup',
];
const noInstructorHugoRoutes = ['/settings'];
const publicUtilityRoutes = ['/api-launch', '/components'];

const pathnameStartsWithRoute = (pathname, route) => pathname === route || pathname.startsWith(`${route}/`);

const AppHeader = () => {
    const { auth } = useAuth();
    const location = useLocation();
    const { device } = useViewport();

    const isNoHeader = noHeaderRoutes.some((route) => {
        return location.pathname.endsWith(route);
    });

    const isPublicUtilityRoute = publicUtilityRoutes.includes(location.pathname);
    const shouldShowLogoOnlyHeader =
        location.pathname.endsWith('/lti-incomplete') ||
        location.pathname.endsWith('/session-expired') ||
        (!auth && !isPublicUtilityRoute);

    if (!shouldShowLogoOnlyHeader && (!auth || isNoHeader)) return null;

    return <HeaderWrapper device={device}>{shouldShowLogoOnlyHeader ? <LogoOnlyHeader /> : <Header />}</HeaderWrapper>;
};

const InstructorHugoAssistant = ({ onFullScreenChange }) => {
    const { auth } = useAuth();
    const location = useLocation();
    const isDisabledRoute = noInstructorHugoRoutes.some((route) => pathnameStartsWithRoute(location.pathname, route));

    if (!['instructor', 'ta'].includes(auth?.role) || isDisabledRoute) return null;

    return <InstructorHugoChat onFullScreenChange={onFullScreenChange} />;
};

const App = () => {
    const { loading } = useAuth();
    const [isHugoFullScreen, setIsHugoFullScreen] = useState(false);

    const handleHugoFullScreenChange = useCallback((isFullScreen) => {
        setIsHugoFullScreen(isFullScreen);
    }, []);

    const renderRoute = () => {
        return <APIRoleBasedRouting />;
    };

    useEffect(() => {
        let observer;

        const updateTitle = () => {
            const h1Elements = document.querySelectorAll('h1');
            if (h1Elements.length === 1) {
                document.title = `${h1Elements[0].textContent} - TimelyGrader`;
            } else if (h1Elements.length > 1) {
                const titles = Array.from(h1Elements)
                    .slice(1)
                    .map((h1) => h1.textContent);
                document.title = `${titles.join(' & ')} - TimelyGrader`;
            }
        };

        observer = new MutationObserver(() => {
            updateTitle();
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true,
        });

        updateTitle();

        return () => {
            if (observer) observer.disconnect();
        };
    }, []);

    return loading ? (
        <Loading />
    ) : (
        <ProviderWrapper>
            <PageWrapper>
                <AppHeader />
                <BodyWrapper data-app-scroll-container isHugoFullScreen={isHugoFullScreen} tabIndex={-1}>
                    {renderRoute()}
                </BodyWrapper>
                <InstructorHugoAssistant onFullScreenChange={handleHugoFullScreenChange} />
            </PageWrapper>
        </ProviderWrapper>
    );
};

const PageWrapper = styled(Stack)(() => {
    return {
        height: '100%',
    };
});

const HeaderWrapper = styled(Box)(({ theme, device }) => {
    return {
        borderBottom: `1px solid ${theme.palette.ensightful.neutral[300]}`,
        background: theme.palette.ensightful.shades[0],
        ...(device !== 'mobile' && {
            zIndex: theme.zIndex.drawer + 1,
        }),
    };
});

const BodyWrapper = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'isHugoFullScreen',
})(({ theme, isHugoFullScreen }) => {
    return {
        flex: 1,
        overflow: isHugoFullScreen ? 'hidden' : 'auto',
        overscrollBehavior: isHugoFullScreen ? 'none' : undefined,
        background: theme.palette.ensightful.neutral[50],
    };
});

export default App;
